/**
 * Zwei reine Hilfsfunktionen fuer die Artikelbild-Anzeige (`components/Artikelbild.tsx`) - ohne
 * DOM-Zugriff, damit sie ohne jsdom testbar bleiben.
 *
 * Der Backend-Endpunkt liefert bereits vollstaendig aus (`backend/src/http/routes/jtl.ts:159-195`,
 * `backend/src/jtl/bilder.ts`) - hier wird nur benutzt, nichts neu erfunden. Sinngemaesses Vorbild
 * ist `web_wms/apps/templates/fba_inbound_warehouse/_artikelbild.html` (Django), nicht
 * zeilengleich uebernommen - hier React 19.
 */

/** Die beiden Groessen, die die Route kennt (`backend/src/jtl/bilder.ts` - `BildGroesse`). */
export type ArtikelbildGroesse = "thumb" | "card";

/**
 * `GET /api/jtl/articles/:kArtikel/image?size=thumb|card`. Liefert `null`, wenn `kArtikel` keine
 * ganze Zahl >= 1 ist (`null`, `undefined`, `0`, negative Werte, Bruchzahlen, Zeichenketten) - dann
 * darf gar nicht erst ein Netzaufruf versucht werden (siehe `Artikelbild.tsx`, "ohne gueltige
 * Kennung: sofort der Platzhalter, kein Netzaufruf").
 */
export function artikelbildUrl(kArtikel: unknown, groesse: ArtikelbildGroesse): string | null {
  if (typeof kArtikel !== "number" || !Number.isInteger(kArtikel) || kArtikel < 1) return null;
  return `/api/jtl/articles/${kArtikel}/image?size=${groesse}`;
}

/**
 * Grauer, neutraler Platzhalter (Andeutung eines Bildrahmens) als `data:image/svg+xml`-URI - genauso
 * aufgebaut wie `supplierBadgeUrl` (`lib/suppliers.ts:125`): `data:image/svg+xml;charset=UTF-8,` +
 * `encodeURIComponent`. Quadratisch mit gesetztem `viewBox`, damit er auf jede Kantenlaenge
 * skaliert.
 *
 * Statisch statt aus dem aktuellen Theme abgeleitet: als `<img src="data:...">` ist die SVG ein
 * eigenstaendiges Dokument OHNE Zugriff auf die Custom Properties der Seite - `var(--ink-faint)`
 * waere darin ungueltig. Und die Funktion soll ohne DOM testbar bleiben, darf also nicht nach dem
 * aktuellen `data-theme` fragen.
 *
 * NACHBESSERUNG (Pruefung 5bdfb6c, Punkt 2): die Grundflaeche ist deshalb `fill="none"` -
 * transparent statt eines woertlich uebernommenen Hell-Farbwerts. Ein fester Wert (vormals `--line`
 * #e4e8f0, der HELL-Wert) traf im Dunkelmodus nicht: dort gilt `--line: #3a3d3f`
 * (`bestand.css:2489-2490`), die Flaeche waere in jeder Tabellenzeile dunkelmodus-untypisch hell
 * geblieben. Durch die Transparenz traegt stattdessen `background: var(--surface-hover)`
 * (`styles/neubau/artikelbild.css`, auf dem `<img>` selbst) - der wechselt mit dem Thema. Nur das
 * Symbol bleibt fest (`#9aa0ad`, liegt nah an `--ink-faint` in BEIDEN Themen).
 */
export function artikelbildPlatzhalter(): string {
  const svg =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">' +
    '<rect width="64" height="64" rx="10" fill="none"/>' +
    '<rect x="14" y="16" width="36" height="28" rx="3" fill="none" stroke="#9aa0ad" stroke-width="3"/>' +
    '<circle cx="24" cy="26" r="4" fill="#9aa0ad"/>' +
    '<path d="M18 40 L29 28 L37 36 L46 23 L50 40 Z" fill="#9aa0ad"/>' +
    "</svg>";
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

/**
 * Liest die JTL-Artikelkennung aus einem Zeilen- oder Trefferobjekt, dessen Feldname NICHT
 * einheitlich ist (Auftrag 2, "Die Zeilenobjekte tragen die JTL-Kennung nicht einheitlich"):
 *
 * - In den Positionslisten (`OrderItem`) heisst sie `kArtikel` (reist aus JTL mit,
 *   `api/jtlBestellungen.ts#alsOrderItem`).
 * - In den Treffern der Artikelsuche der Anlagemaske (`components/App/App.tsx#anlagemaskeArtikelSuche`)
 *   heisst sie `id` und ist bereits `String(kArtikel)` - eine Zeichenkette.
 *
 * `kArtikel` gewinnt, falls BEIDE Felder vorkommen sollten; `id` ist nur der Ersatz. Akzeptiert
 * sowohl Zahl als auch Zeichenkette und gibt `null` zurueck, sobald daraus keine ganze Zahl >= 1
 * wird - dieselbe Schranke wie `artikelbildUrl`, hier nur VOR der Typpruefung auf `unknown`
 * angewendet, damit die Aufrufstellen selbst nicht zwischen den beiden Feldnamen unterscheiden
 * muessen.
 */
export function artikelKennung(objekt: unknown): number | null {
  if (objekt === null || typeof objekt !== "object") return null;
  const quelle = objekt as Record<string, unknown>;
  const roh = "kArtikel" in quelle && quelle.kArtikel != null ? quelle.kArtikel : quelle.id;
  const zahl = typeof roh === "string" ? Number(roh) : roh;
  if (typeof zahl !== "number" || !Number.isInteger(zahl) || zahl < 1) return null;
  return zahl;
}
